import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIeKitLabels } from '@/hooks/useIeKitLabels';
import type { ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import RemoveIcon from '@mui/icons-material/Remove';
import { alpha } from '@mui/material/styles';
import { PricingCell } from './PricingCell';
import {
  useIEMBuyProgramCatalog,
  usePHRItems,
  useIESummary,
} from '@/hooks/queries/iem/useIEMQueries';
import type { IEMBuyProgramCatalogItem } from '@/services/iem';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useNotifications, useNotificationStore } from '@/stores/useNotificationStore';
import { extractErrorMessage } from '../workflowUtils';
import type { IssueBuyProgramCartItem } from './types';
import { extractAuthorizedCatalogIds, filterAuthorizedBuyProgramItems } from './buyProgramUtils';
import { validatePricingInputs, parsePricingInput } from './buyProgramPricing';
import {
  EXCLUDE_PNP_CATALOG_TYPE_KEEP_NULL,
  EXCLUDE_PNP_TYPE_KEEP_NULL,
} from '@/utils/iemFilters';

/** Pricing data from existing PHR items */
type ExistingPricing = {
  actualPrice: number | null;
  tax: number | null;
  shipping: number | null;
};

/** Default empty pricing state for form inputs */
const EMPTY_PRICING = { actualPrice: '', tax: '', shipping: '' } as const;

export interface IssueBuyProgramTabResult {
  toolbar: ReactNode | null;
  content: ReactNode;
  cartItems: IssueBuyProgramCartItem[];
  actions: IssueBuyProgramTabActions;
}

export interface IssueBuyProgramTabOptions {
  open: boolean;
  active?: boolean;
  customerId?: string;
  readOnly?: boolean;
}

export interface IssueBuyProgramTabActions {
  removeItem: (itemId: string) => void;
  adjustQuantity: (itemId: string, delta: number) => void;
  clear: () => void;
}

export const useIssueBuyProgramTab = ({
  open,
  active = true,
  customerId,
  readOnly = false,
}: IssueBuyProgramTabOptions): IssueBuyProgramTabResult => {
  const isTabActive = open && active;
  const notifications = useNotifications();
  const { label: ieKitLabel, labelPlural: ieKitLabelPlural } = useIeKitLabels('front_window');
  const activeNotifications = useNotificationStore((state) => state.notifications);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 350);
  const [buyProgramItems, setBuyProgramItems] = useState<IssueBuyProgramCartItem[]>([]);
  const [issueQuantities, setIssueQuantities] = useState<Record<string, number>>({});
  const [selectedKit, setSelectedKit] = useState<string>('all');
  // State for inline pricing inputs (per item row)
  const [itemPricing, setItemPricing] = useState<
    Record<string, { actualPrice: string; tax: string; shipping: string }>
  >({});

  const {
    data = [],
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useIEMBuyProgramCatalog({
    searchTerm: debouncedSearch,
    enabled: isTabActive,
  });

  const {
    data: existingPhrItems = [],
    isLoading: phrLoading,
    isFetching: phrFetching,
  } = usePHRItems(customerId ?? '', {
    status: 'neq.closed',
    ...EXCLUDE_PNP_TYPE_KEEP_NULL,
  }, isTabActive);

  const { data: ieItems = [], isLoading: ieLoading } = useIESummary(
    customerId ?? '',
    EXCLUDE_PNP_CATALOG_TYPE_KEEP_NULL,
    isTabActive,
  );
  const buyProgramIeItems = useMemo(
    () => ieItems.filter((item) => item.catalogType === 'buy program'),
    [ieItems],
  );

  // Build set of catalog IDs authorized via IE Kit
  const authorizedCatalogIds = useMemo(() => extractAuthorizedCatalogIds(ieItems), [ieItems]);

  // Filter Buy Program catalog to only show authorized items
  const authorizedBuyProgramItems = useMemo(
    () => filterAuthorizedBuyProgramItems(data, authorizedCatalogIds),
    [data, authorizedCatalogIds],
  );

  // Map catalogId -> Set of Kit Names
  const kitNamesByCatalogId = useMemo(() => {
    const map = new Map<string, Set<string>>();
    buyProgramIeItems.forEach((item) => {
      if (item.catalogId && item.kitName) {
        const existing = map.get(item.catalogId) ?? new Set();
        existing.add(item.kitName);
        map.set(item.catalogId, existing);
      }
    });
    return map;
  }, [buyProgramIeItems]);

  // Map catalogId -> first ieKitId (a buy program catalog is only in one kit per soldier)
  const ieKitIdByCatalogId = useMemo(() => {
    const map = new Map<string, string>();
    buyProgramIeItems.forEach((item) => {
      if (item.catalogId && item.ieKitId && !map.has(item.catalogId)) {
        map.set(item.catalogId, item.ieKitId);
      }
    });
    return map;
  }, [buyProgramIeItems]);

  // Calculate IE Kit stats (authorized vs current) by catalogId
  const ieStatsByCatalogId = useMemo(() => {
    const map = new Map<string, { authorizedQty: number; currentQty: number }>();
    buyProgramIeItems.forEach((item) => {
      if (item.catalogId) {
        const existing = map.get(item.catalogId) ?? {
          authorizedQty: 0,
          currentQty: item.currentQty,
        };
        // Accumulate authorizedQty if the item appears in multiple kits
        // Note: currentQty comes from the view which aggregates per person/catalog, so it should be same across rows
        // We use Math.max for currentQty just to be safe, though it should be identical.
        map.set(item.catalogId, {
          authorizedQty: existing.authorizedQty + item.authorizedQty,
          currentQty: Math.max(existing.currentQty, item.currentQty),
        });
      }
    });
    return map;
  }, [buyProgramIeItems]);

  // Extract unique available kits for filtering
  const availableKits = useMemo(() => {
    const kits = new Set<string>();
    buyProgramIeItems.forEach((item) => {
      if (item.kitName) {
        kits.add(item.kitName);
      }
    });
    return Array.from(kits).sort();
  }, [buyProgramIeItems]);

  useEffect(() => {
    if (!open) {
      setSearch('');
      setBuyProgramItems([]);
      setIssueQuantities({});
      setSelectedKit('all');
      setItemPricing({});
    }
  }, [open]);

  const selectedIds = useMemo(
    () => new Set(buyProgramItems.map((item) => item.id)),
    [buyProgramItems],
  );

  const existingKeys = useMemo(() => {
    const normalize = (value?: string | null): string | null => {
      if (!value) return null;
      const trimmed = value.trim();
      return trimmed ? trimmed.toUpperCase() : null;
    };

    const keys = new Set<string>();
    existingPhrItems.forEach((item) => {
      const key = normalize(item.lin) ?? normalize(item.catalogId) ?? normalize(item.nsn);
      if (key) {
        keys.add(key);
      }
    });
    return keys;
  }, [existingPhrItems]);

  // Map catalogId -> pricing from existing PHR items (for displaying pricing of assigned items)
  // Merges pricing fields from multiple PHR items if they have partial data
  const existingPricingByCatalogId = useMemo(() => {
    const map = new Map<string, ExistingPricing>();
    existingPhrItems.forEach((item) => {
      if (!item.catalogId) return;
      const existing = map.get(item.catalogId);
      if (!existing) {
        // First entry for this catalog - use its values
        map.set(item.catalogId, {
          actualPrice: item.actualPrice ?? null,
          tax: item.tax ?? null,
          shipping: item.shipping ?? null,
        });
      } else {
        // Merge: fill in any null fields from this item
        map.set(item.catalogId, {
          actualPrice: existing.actualPrice ?? item.actualPrice ?? null,
          tax: existing.tax ?? item.tax ?? null,
          shipping: existing.shipping ?? item.shipping ?? null,
        });
      }
    });
    return map;
  }, [existingPhrItems]);

  const handleAddItem = useCallback(
    (item: IEMBuyProgramCatalogItem) => {
      if (readOnly) return;

      // Get pricing from state and parse values
      const pricing = itemPricing[item.id] ?? EMPTY_PRICING;
      const actualPrice = parsePricingInput(pricing.actualPrice);
      const tax = parsePricingInput(pricing.tax);
      const shipping = parsePricingInput(pricing.shipping);

      // Validate pricing inputs using shared utility
      const validation = validatePricingInputs(actualPrice, tax, shipping, item.unitPrice);
      if (!validation.valid) {
        notifications.warning(validation.error);
        return;
      }

      const normalizedKey = [item.lin, item.nsn].find(Boolean)?.toUpperCase();
      const alreadyIssued = normalizedKey ? existingKeys.has(normalizedKey) : false;

      if (alreadyIssued) {
        notifications.warning('Member already has this Buy Program item');
      }

      setBuyProgramItems((current) => {
        const quantity = Math.max(1, issueQuantities[item.id] ?? 1);
        const existingIndex = current.findIndex((entry) => entry.id === item.id);

        if (existingIndex >= 0) {
          const next = [...current];
          const existingItem = next[existingIndex];
          if (existingItem) {
            next[existingIndex] = {
              ...existingItem,
              quantity: existingItem.quantity + quantity,
              // Update pricing with latest values
              actualPrice,
              tax,
              shipping,
            };
          }
          return next;
        }

        const nextItem: IssueBuyProgramCartItem = {
          id: item.id,
          catalog: item,
          quantity,
          maxQuantity: Math.max(
            0,
            (ieStatsByCatalogId.get(item.id)?.authorizedQty ?? 0) -
              (ieStatsByCatalogId.get(item.id)?.currentQty ?? 0),
          ),
          actualPrice,
          tax,
          shipping,
          ieKitId: ieKitIdByCatalogId.get(item.id) ?? null,
        };
        return [...current, nextItem];
      });
      // Reset quantity to 1, but retain pricing inputs for potential additional adds
      setIssueQuantities((prev) => ({ ...prev, [item.id]: 1 }));
      // NOTE: Pricing inputs are intentionally retained so users can easily add more
      // of the same item with the same pricing
    },
    [
      notifications,
      existingKeys,
      readOnly,
      issueQuantities,
      ieStatsByCatalogId,
      ieKitIdByCatalogId,
      itemPricing,
    ],
  );

  const handleRemoveItem = useCallback(
    (itemId: string) => {
      if (readOnly) return;
      setBuyProgramItems((current) => current.filter((item) => item.id !== itemId));
    },
    [readOnly],
  );

  const handleAdjustQuantity = useCallback(
    (itemId: string, delta: number) => {
      if (readOnly) return;

      const item = buyProgramItems.find((i) => i.id === itemId);
      if (!item) return;

      if (delta > 0) {
        const stats = ieStatsByCatalogId.get(itemId);
        // Fallback if stats not found (should not happen for authorized items)
        const { authorizedQty, currentQty } = stats ?? { authorizedQty: 999999, currentQty: 0 };
        const remainingAuthorized = Math.max(0, authorizedQty - currentQty);
        const proposedQty = item.quantity + delta;

        // If increasing, enforce IE Kit limit
        if (proposedQty > remainingAuthorized) {
          const warningTitle = 'Cannot issue more than authorized quantity.';
          // Prevent duplicate warnings
          const alreadyShowing = activeNotifications.some(
            (n) => n.type === 'warning' && n.title === warningTitle,
          );

          if (!alreadyShowing) {
            notifications.warning(warningTitle);
          }
          return;
        }
      }

      setBuyProgramItems((current) =>
        current.map((entry) => {
          if (entry.id === itemId) {
            return { ...entry, quantity: Math.max(1, entry.quantity + delta) };
          }
          return entry;
        }),
      );
    },
    [readOnly, ieStatsByCatalogId, notifications, buyProgramItems, activeNotifications],
  );

  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  const toolbar = useMemo(() => {
    return (
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
      >
        <Box>
          <Typography variant="subtitle2">Authorized Buy Program Items</Typography>
          <Typography variant="body2" color="text.secondary">
            Buy Program items authorized for this member via their {ieKitLabel}.
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            select
            size="small"
            label="Filter by Kit"
            value={selectedKit}
            onChange={(e) => setSelectedKit(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="all">All {ieKitLabelPlural}</MenuItem>
            {availableKits.map((kit) => (
              <MenuItem key={kit} value={kit}>
                {kit}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Search buy program catalog"
            placeholder="Search by LIN, NSN, or name"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape' && search) {
                event.preventDefault();
                event.stopPropagation();
                setSearch('');
              }
            }}
            InputProps={{
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch('')} aria-label="Clear search">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
            sx={{ width: { xs: '100%', sm: 320 } }}
          />
        </Stack>
      </Stack>
    );
  }, [search, selectedKit, availableKits]);

  const content = useMemo(() => {
    if (!open) {
      return <Box sx={{ minHeight: 240 }} />;
    }

    const loadingSection = (
      <Paper
        variant="outlined"
        sx={{ minHeight: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <CircularProgress size={24} />
      </Paper>
    );

    if (isLoading || ieLoading) {
      return <Stack spacing={2}>{loadingSection}</Stack>;
    }

    // Filter rows based on selected kit
    const rows = authorizedBuyProgramItems
      .filter((item) => {
        if (selectedKit === 'all') return true;
        const itemKits = kitNamesByCatalogId.get(item.id);
        return itemKits?.has(selectedKit) ?? false;
      })
      .sort((a, b) => {
        const left = (a.name ?? '').trim();
        const right = (b.name ?? '').trim();
        const nameCompare = left.localeCompare(right, undefined, { sensitivity: 'base' });
        if (nameCompare !== 0) {
          return nameCompare;
        }
        return (a.lin ?? '').localeCompare(b.lin ?? '', undefined, { sensitivity: 'base' });
      });

    const tableSection = (
      <Paper variant="outlined" sx={{ overflow: 'hidden', maxHeight: '70vh' }}>
        {isFetching && !isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : null}
        {isError ? (
          <Box sx={{ p: 3 }}>
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={handleRetry}>
                  Retry
                </Button>
              }
            >
              {extractErrorMessage(error, 'Unable to load buy program catalog items.')}
            </Alert>
          </Box>
        ) : (
          <Box sx={{ overflow: 'auto', maxHeight: '70vh' }}>
            <Table size="small" aria-label="Buy program catalog">
              <TableHead>
                <TableRow>
                  <TableCell>{ieKitLabel}</TableCell>
                  <TableCell>LIN</TableCell>
                  <TableCell>NSN</TableCell>
                  <TableCell>Nomenclature</TableCell>
                  <TableCell align="right">{ieKitLabel} Qty</TableCell>
                  <TableCell align="right">HR Qty</TableCell>
                  <TableCell align="right">Max Price</TableCell>
                  <TableCell align="right">Actual Price</TableCell>
                  <TableCell align="center" sx={{ minWidth: 90 }}>Tax</TableCell>
                  <TableCell align="right">Shipping</TableCell>
                  <TableCell align="right">Issue Qty</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={13} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {authorizedBuyProgramItems.length === 0
                          ? 'No authorized Buy Program items for this member.'
                          : 'No items found for the selected kit filter.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((item) => {
                    const lin = item.lin ?? '—';
                    const nsn = item.nsn ?? '—';
                    const isSelected = selectedIds.has(item.id);

                    const stats = ieStatsByCatalogId.get(item.id) ?? {
                      authorizedQty: 0,
                      currentQty: 0,
                    };
                    const { authorizedQty, currentQty } = stats;

                    // Find quantity already in cart
                    const cartItem = buyProgramItems.find((ci) => ci.id === item.id);
                    const qtyInCart = cartItem?.quantity ?? 0;

                    const highlightRow = currentQty > 0 || qtyInCart > 0;
                    const issueQty = issueQuantities[item.id] ?? 1;

                    // Remaining allowed is authorized - (on_hand + in_cart)
                    const remainingAuthorized = Math.max(0, authorizedQty - currentQty - qtyInCart);

                    // Determine status chip
                    let statusLabel = 'Not Issued';
                    let statusColor: 'default' | 'success' | 'warning' | 'info' = 'default';

                    if (currentQty >= authorizedQty) {
                      statusLabel = 'Fully Assigned';
                      statusColor = 'success';
                    } else if (currentQty > 0) {
                      statusLabel = `Assigned ${currentQty}`;
                      statusColor = 'info';
                    } else {
                      statusLabel = 'Not Assigned';
                      statusColor = 'default';
                    }

                    const kits =
                      Array.from(kitNamesByCatalogId.get(item.id) ?? []).join(', ') || '—';

                    const isLimitExceeded = issueQty > remainingAuthorized;
                    const canAdd = !isLimitExceeded && remainingAuthorized > 0;

                    // For fully assigned items, show existing pricing as read-only text
                    const existingPricing = existingPricingByCatalogId.get(item.id);
                    const showExistingPricing = currentQty > 0 && remainingAuthorized <= 0 && qtyInCart <= 0;

                    const actionButton = (
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleAddItem(item)}
                        disabled={phrLoading || phrFetching || readOnly || !canAdd}
                      >
                        <AddIcon fontSize="inherit" />
                      </IconButton>
                    );

                    const wrappedAddAction =
                      phrLoading || phrFetching ? (
                        <Tooltip title="Checking existing hand receipts…">
                          <span>{actionButton}</span>
                        </Tooltip>
                      ) : remainingAuthorized <= 0 ? (
                        <Tooltip title="Member has reached the authorized quantity.">
                          <span>{actionButton}</span>
                        </Tooltip>
                      ) : isLimitExceeded ? (
                        <Tooltip title={`Cannot issue more than ${remainingAuthorized} items.`}>
                          <span>{actionButton}</span>
                        </Tooltip>
                      ) : (
                        actionButton
                      );

                    return (
                      <TableRow
                        key={item.id}
                        hover
                        sx={
                          highlightRow
                            ? {
                                backgroundColor: (theme) =>
                                  alpha(theme.palette.warning.light, 0.12),
                                '&:hover': {
                                  backgroundColor: (theme) =>
                                    alpha(theme.palette.warning.light, 0.18),
                                },
                              }
                            : undefined
                        }
                      >
                        <TableCell>
                          <Typography variant="body2">{kits}</Typography>
                        </TableCell>
                        <TableCell>{lin}</TableCell>
                        <TableCell>{nsn}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {item.name}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{authorizedQty}</TableCell>
                        <TableCell align="right">{currentQty}</TableCell>
                        <TableCell align="right">
                          {item.unitPrice != null ? `$${item.unitPrice.toFixed(2)}` : '—'}
                        </TableCell>
                        <PricingCell
                          value={existingPricing?.actualPrice}
                          isReadOnly={showExistingPricing}
                          inputValue={itemPricing[item.id]?.actualPrice ?? ''}
                          onChange={(value) => {
                            setItemPricing((prev) => ({
                              ...prev,
                              [item.id]: {
                                ...(prev[item.id] ?? EMPTY_PRICING),
                                actualPrice: value,
                              },
                            }));
                          }}
                          disabled={readOnly || (remainingAuthorized <= 0 && qtyInCart <= 0)}
                          width="100px"
                          showError={
                            itemPricing[item.id]?.actualPrice !== '' &&
                            item.unitPrice != null &&
                            parseFloat(itemPricing[item.id]?.actualPrice || '0') > item.unitPrice
                          }
                        />
                        <PricingCell
                          value={existingPricing?.tax}
                          isReadOnly={false}
                          inputValue={itemPricing[item.id]?.tax ?? ''}
                          onChange={(value) => {
                            setItemPricing((prev) => ({
                              ...prev,
                              [item.id]: {
                                ...(prev[item.id] ?? EMPTY_PRICING),
                                tax: value,
                              },
                            }));
                          }}
                          disabled={readOnly}
                          width="90px"
                        />
                        <PricingCell
                          value={existingPricing?.shipping}
                          isReadOnly={showExistingPricing}
                          inputValue={itemPricing[item.id]?.shipping ?? ''}
                          onChange={(value) => {
                            setItemPricing((prev) => ({
                              ...prev,
                              [item.id]: {
                                ...(prev[item.id] ?? EMPTY_PRICING),
                                shipping: value,
                              },
                            }));
                          }}
                          disabled={readOnly || (remainingAuthorized <= 0 && qtyInCart <= 0)}
                          width="90px"
                        />
                        <TableCell align="right">
                          <TextField
                            type="number"
                            size="small"
                            value={issueQty}
                            onChange={(e) => {
                              const val = Math.max(1, parseInt(e.target.value) || 0);
                              // Enforce limit in input
                              const cappedVal = Math.min(val, Math.max(1, remainingAuthorized));
                              setIssueQuantities((prev) => ({ ...prev, [item.id]: cappedVal }));
                            }}
                            disabled={readOnly || remainingAuthorized <= 0}
                            slotProps={{
                              htmlInput: {
                                min: 1,
                                max: Math.max(1, remainingAuthorized),
                                style: { textAlign: 'right' },
                              },
                            }}
                            sx={{
                              width: '70px',
                              '& .MuiOutlinedInput-root': {
                                bgcolor: 'background.paper',
                                '&:not(.Mui-disabled)': {
                                  bgcolor: 'background.paper',
                                },
                              },
                              '& .MuiOutlinedInput-input': {
                                py: 0.75,
                              },
                            }}
                            error={isLimitExceeded}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={statusLabel}
                            color={statusColor}
                            variant="filled"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            {isSelected ? (
                              <Tooltip title="Remove from cart">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleRemoveItem(item.id)}
                                  disabled={readOnly}
                                >
                                  <RemoveIcon fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                            ) : null}
                            {wrappedAddAction}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Box>
        )}
      </Paper>
    );

    return <Stack spacing={2}>{tableSection}</Stack>;
  }, [
    open,
    isLoading,
    ieLoading,
    isFetching,
    authorizedBuyProgramItems,
    isError,
    error,
    handleRetry,
    handleAddItem,
    handleRemoveItem,
    phrLoading,
    phrFetching,
    readOnly,
    selectedIds,
    issueQuantities,
    selectedKit,
    kitNamesByCatalogId,
    ieStatsByCatalogId,
    buyProgramItems,
    itemPricing,
    existingPricingByCatalogId,
  ]);

  return {
    toolbar,
    content,
    cartItems: buyProgramItems,
    actions: useMemo(
      () => ({
        removeItem: handleRemoveItem,
        adjustQuantity: handleAdjustQuantity,
        clear: () => setBuyProgramItems([]),
      }),
      [handleRemoveItem, handleAdjustQuantity],
    ),
  };
};
